import React, { useState, useEffect } from "react";
//components material ui lib
import "../../Pages/StockChart/stockChartIndicator.css";
import { Grid, Typography, FormControl, InputLabel, Select, MenuItem, Box, CircularProgress } from "@mui/material";

import MaterialFormComponent from "../../components/FormComponent/MaterialFormComponet";
import PCPFileUpload from "./PCPFileUpload";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import DownloadIcon from "@mui/icons-material/Download";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { styled } from "@mui/material/styles";

const AddScreenData = (props: any) => {
  //state variables
  const [expanded, setExpanded] = React.useState<string | false>("panel1");
  const [open, setOpen] = React.useState(false); // open modal state
  const [showData, setShowData] = useState(false); // default not showing data condition state
  const [password, setPassword] = useState(""); // input filed of password state
  const [visible, setVisible] = useState(""); // valadations displayed state
  
  // Download modal states
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState("PCP");
  const [selectedTimeframe, setSelectedTimeframe] = useState("day");
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  // Available symbols and timeframes
  const symbols = [
   "PCP", "PCP103", "PCP106", "PCP107", "PCP111", 
    "PCP112", "PCP122", "QQQ", "SPXL", "SPXU", "SPY", "SQQQ", 
    "TNA", "TQQQ", "TZA", "IWM", "NAIL"
  ];

  const timeframes = [
    { value: "hour", label: "Hour" },
    { value: "day", label: "Day" },
    { value: "week", label: "Week" },
    { value: "month", label: "Month" },
    { value: "quarter", label: "Quarter" },
    { value: "year", label: "Year" }
  ];

  const Accordion = styled((props: AccordionProps) => (
    <MuiAccordion disableGutters elevation={0} square {...props} />
  ))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&::before": {
      display: "none",
    },
  }));

  const AccordionSummary = styled((props: AccordionSummaryProps) => (
    <MuiAccordionSummary
      expandIcon={
        <ArrowForwardIosSharpIcon
          sx={{ fontSize: "0.9rem", color: "#0DA718" }}
        />
      }
      {...props}
    />
  ))(({ theme }) => ({
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, .05)"
        : "rgba(0, 0, 0, .03)",
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
      transform: "rotate(90deg)",
    },
    "& .MuiAccordionSummary-content": {
      marginLeft: theme.spacing(1),
    },
  }));

  const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
  }));

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  // Download modal handlers
  const handleDownloadClick = () => {
    setDownloadModalOpen(true);
    setDownloadError("");
  };

  const handleDownloadClose = () => {
    setDownloadModalOpen(false);
    setDownloadError("");
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError("");

    try {
      const response = await fetch('https://ceiak6b9aa.execute-api.us-west-2.amazonaws.com/test/pcpdownload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          symbol: selectedSymbol,
          timeframe: selectedTimeframe
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      
      // Parse the nested JSON structure
      let data;
      if (responseData.body) {
        // If body is a string, parse it
        if (typeof responseData.body === 'string') {
          data = JSON.parse(responseData.body);
        } else {
          data = responseData.body;
        }
      } else {
        data = responseData;
      }

      // Extract the actual data array
      const stockData = data.data || data;
      
      if (!Array.isArray(stockData) || stockData.length === 0) {
        throw new Error('No data available for download');
      }

      // Convert to CSV format
      const csvHeaders = ['Date', 'Open', 'High', 'Low', 'Close', 'Symbol', 'TimeFrame'];
      const csvRows = [csvHeaders];

      stockData.forEach(item => {
        const date = new Date(item.u_Date || item.Date).toISOString().split('T')[0];
        csvRows.push([
          date,
          item.Open || '',
          item.High || '',
          item.Low || '',
          item.Close || '',
          item.symbol || selectedSymbol,
          item.timeFrame || selectedTimeframe
        ]);
      });

      // Convert to CSV string
      const csvContent = csvRows.map(row => 
        row.map(field => `"${field}"`).join(',')
      ).join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedSymbol}_${selectedTimeframe}_data.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setDownloadModalOpen(false);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadError('Failed to download data. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleReset = () => {
    setSelectedSymbol("PCP");
    setSelectedTimeframe("day");
    setDownloadError("");
  };

  useEffect(() => {
    let pass = localStorage.getItem("user_password");
    let passTime: any = localStorage.getItem("date");
    if (pass === null && passTime === null) {
      setOpen(true);
    } else {
      setShowData(true);
      //localStorage.clear()
    }

    const intervalId = setInterval(() => {
      let tim = new Date().getTime();
      //console.log('min---->232342',tim,"and",passTime);

      let d = Math.floor((tim - passTime) / 1000);
      //console.log("sub===>",d)
      if (d > 10800) {
        localStorage.clear();
        setOpen(true);
        setShowData(false);
      }
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const handleClose = () => {
    if (password === "") {
      setVisible("empty");
    } else if (password !== "P99C@422853n$") {
      setVisible("wrong");
    } else if (password === "P99C@422853n$") {
      setShowData(true);
      setOpen(false);
      setVisible("");
      setPassword("");
      //set password and login time in local stroage
      localStorage.setItem("user_password", "P99C@422853n$");
      let timeD = new Date();
      ///console.log("check===>1",timeD,"and","and time",timeD.getTime())
      localStorage.setItem("date", timeD.getTime().toString());
    }
  };

  return (
    <div>
      <div>
        <Dialog open={open}>
          <DialogTitle>Password required</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This site is password protected. Please enter a password.
            </DialogContentText>
            <TextField
              margin="dense"
              id="name"
              type="password"
              fullWidth
              variant="standard"
              value={password}
              onChange={(e) => {
                e.preventDefault();
                setPassword(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  document.getElementById("myBtn")?.click();
                }
              }}
            />
            {visible === "empty" ? (
              <Typography color={"red"} fontSize={14}>
                Password required*
              </Typography>
            ) : null}
            {visible === "wrong" ? (
              <Typography color={"red"} fontSize={14}>
                Please enter correct password
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button id="myBtn" onClick={handleClose} variant="contained">
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </div>

      {/* Download Modal */}
      <Dialog 
        open={downloadModalOpen} 
        onClose={handleDownloadClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'left', color: '#0DA718' }}>
          ADD PCP DATA
        </DialogTitle>
                 <DialogContent>
           <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, color: '#666' }}>
             PCP Form Data
           </Typography>
           
           <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
             <FormControl sx={{ flex: 1 }}>
               <InputLabel id="symbol-label">Select Symbol</InputLabel>
               <Select
                 labelId="symbol-label"
                 value={selectedSymbol}
                 label="Select Symbol"
                 onChange={(e) => setSelectedSymbol(e.target.value)}
               >
                 {symbols.map((symbol) => (
                   <MenuItem key={symbol} value={symbol}>
                     {symbol}
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>

             <FormControl sx={{ flex: 1 }}>
               <InputLabel id="timeframe-label">Time Frame</InputLabel>
               <Select
                 labelId="timeframe-label"
                 value={selectedTimeframe}
                 label="Time Frame"
                 onChange={(e) => setSelectedTimeframe(e.target.value)}
               >
                 {timeframes.map((tf) => (
                   <MenuItem key={tf.value} value={tf.value}>
                     {tf.label}
                   </MenuItem>
                 ))}
               </Select>
             </FormControl>
           </Box>

           {downloadError && (
             <Typography color="error" sx={{ textAlign: 'center', mt: 2 }}>
               {downloadError}
             </Typography>
           )}
         </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
          <Button 
            onClick={handleReset} 
            color="error"
            disabled={isDownloading}
          >
            RESET
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              onClick={handleDownloadClose} 
              color="primary"
              disabled={isDownloading}
            >
              CLOSE
            </Button>
            <Button
              onClick={handleDownload}
              variant="contained"
              color="success"
              startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              disabled={isDownloading}
              sx={{ color: 'white' }}
            >
              {isDownloading ? 'DOWNLOADING...' : 'DOWNLOAD'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <div
        style={{
          backgroundColor: "#0DA718",
          paddingTop: "20px",
          paddingBottom: "20px",
          position: "fixed",
          top: 58,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "auto",
        }}
      >
        <div>
          <Grid
            container
            spacing={2}
            sx={{
              border: "2px solid #ECECEC",
              width: "80%",
              margin: "auto",
              padding: "15px",
              borderRadius: "10px",
              boxShadow: "0 0 10px rgba(0,0,0,0.1)",
              backgroundColor: "white",
            }}
          >
            <div>
              <Accordion
                expanded={expanded === "panel1"}
                onChange={handleChange("panel1")}
              >
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  sx={{
                    // backgroundColor: "#bebab3",
                    color: "#0DA718 !important",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ marginTop: "5px" }}> ADD PCP DATA</Typography>

                  <Button
                    onClick={handleDownloadClick}
                    variant="outlined"
                    color="success"
                    className=""
                    sx={{
                      marginLeft: "auto",
                    }}
                  >
                    {" "}
                    DOWNLOAD
                  </Button>
                </AccordionSummary>
                <AccordionDetails>
                  <MaterialFormComponent />
                </AccordionDetails>
              </Accordion>
              {/* <Accordion
                expanded={expanded === "panel2"}
                onChange={handleChange("panel2")}
              >
                <AccordionSummary
                  aria-controls="panel2d-content"
                  id="panel2d-header"
                  sx={{
                    // backgroundColor: "#0DA718",
                    color: "#0DA718 !important",
                  }}
                >
                  <Typography>UPLOAD PCP DATA CSV FILE</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <PCPFileUpload />
                </AccordionDetails>
              </Accordion> */}
            </div>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default AddScreenData;
